import React, { useRef } from "react";
import dayjs from "dayjs";

import { Container, Grid, FormControl, FormGroup, FormHelperText, Card, CardContent, Typography, Button, Box, LinearProgress } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";

import YouTubeIcon from "@mui/icons-material/YouTube";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import CustomInput from "../../../../helper-components/input/CustomInput";
import CustomStepper from "../../../../helper-components/form/CustomStepper";
import ConfirmationDialog from "../../../../helper-components/input/ConfirmationDialog";
import TransparentFragment from "../../../../helper-components/misc/TransparentFragment";

import CustomSelection from "../../../../helper-components/form/CustomSelection";

import { useDialog } from "../../../../helper-functions/hooks/useDialog";
import { querySelectAuthors } from "../../../../helper-functions/api/authorQueries";
import { queryCookieStepper } from "../../../../helper-functions/api/cookieQueries";
import { RESOURCE_VALIDATION } from "../../../../helper-functions/validation/resourceValidation";
import { addFormToObj, formatDate, getEllipsis, isEmpty, isVimeo, isYoutube, setInsertObjectState, setValue } from "../../../../helper-functions/shared/miscUtils";
import { formHasErrors, formHasValue, validateRow } from "../../../../helper-functions/validation/objectValidation";

import { API_RESOURCES_UPLOAD, COOKIE_REQUEST_OBJECT, COOKIE_TYPES, RESOURCE_REQUEST_OBJECT, RESOURCE_TYPES } from "../../../../config/constants/Constants";
import { createUrl } from "../../../../helper-functions/api/apiUtils";

/**
 * A stepper component to add a new resource.
 * Includes helper descriptions and validation.
 */
export default function ResourceAddStepper(props) {
    const { enqueueDialog, setOpen, ...options } = useDialog(); // Nested dialog component.
    const fileInputRef = useRef(null);

    const status = props.backend.programStatus;

    // Because modals don't re-render if the props' state changes, use a callback.
    const { callback, row: existingRow = {} } = props;

    const object = RESOURCE_REQUEST_OBJECT;
    const validation = RESOURCE_VALIDATION;

    const [row, setRow] = React.useState(addFormToObj(JSON.parse(JSON.stringify(object))));

    // Source selection state
    const [sourceType, setSourceType] = React.useState(""); // "youtube", "vimeo", "upload"

    // File upload state
    const [selectedFile, setSelectedFile] = React.useState(null);
    const [uploadProgress, setUploadProgress] = React.useState(0);
    const [uploadStatus, setUploadStatus] = React.useState(""); // "idle", "uploading", "success", "error"

    // Cookie state logic.
    const cookieObject = COOKIE_REQUEST_OBJECT;

    const [recentCookie, setRecentCookie] = React.useState({});
    const [cookieRow, setCookieRow] = React.useState(addFormToObj(JSON.parse(JSON.stringify(cookieObject))));

    // Initialize the temporary row object with existing values.
    React.useEffect(() => {
        const newRow = {
            ...row,
            ...existingRow,

            uploaded_on: existingRow.uploaded_on ? dayjs(existingRow.uploaded_on) : null,

            form: {
                ...row.form,
                ...existingRow.form,
                saved: false // Required to allow editing input fields.
            }
        };

        setValue(newRow, "type", "UNCATEGORIZED");
        setInsertObjectState(setRow, newRow);
    }, []);

    // Automatically set the cookie type.
    React.useEffect(() => {
        if (isYoutube(row?.url)) {
            onCookieChange(cookieRow, "type", "YOUTUBE.COM");
        } else if (isVimeo(row?.url)) {
            onCookieChange(cookieRow, "type", "VIMEO.COM");
        }
    }, [row]);

    // Save the latest cookie object.
    React.useEffect(() => {
        if (!cookieRow?.type) return;

        const type = cookieRow.type;
        if (recentCookie[type]) return;

        queryCookieStepper(props, { type }, (response) => {
            setRecentCookie((prevState) => ({
                ...prevState,
                [type]: response
            }));
        });
    }, [cookieRow]);

    /**
     * Executed when typing something into an input field.
     */
    const onChange = (obj, key, value) => {
        const newRow = JSON.parse(JSON.stringify(row));
        const newValue = ["url", "_referer_url"].includes(key) && value ? value.trim() : value;

        setValue(newRow, key, newValue);

        if (key === "_file_name") {
            newRow.meta_data.file_name = newValue;
        }

        if (key === "_referer_url") {
            newRow.meta_data.referer_url = newValue;
        }

        validateRow(obj, validation, newRow);
        setInsertObjectState(setRow, newRow);
    };

    const handleAuthors = (selected = []) => {
        const ids = selected.map((el) => el.id);
        onChange(row, "author_ids", ids);
    };

    const handleClick = (confirm) => {
        const options = { row, cookieRow };

        callback(confirm, options);
    };

    const onCookieChange = (obj, key, value) => {
        const newRow = JSON.parse(JSON.stringify(cookieRow));
        setValue(newRow, key, value);

        setInsertObjectState(setCookieRow, newRow);
    };

    // Handle file selection
    const handleFileSelect = (event) => {
        const file = event.target.files[0];

        if (file) {
            setSelectedFile(file);

            // Update the resource name with the file name by default
            onChange(row, "name", file.name.split(".").slice(0, -1).join("."));
            setUploadStatus("idle");
        }
    };

    // Handle file upload
    const handleFileUpload = async () => {
        if (!selectedFile) return;

        setUploadStatus("uploading");
        setUploadProgress(0);

        const formData = new FormData();
        formData.append("file", selectedFile);

        const jwt = props.user.userJWT;
        const url = createUrl({ path: API_RESOURCES_UPLOAD });

        return new Promise((resolve) => {
            try {
                const xhr = new XMLHttpRequest();
                xhr.open("POST", url);

                if (jwt) {
                    xhr.setRequestHeader("Authorization", `Bearer ${jwt}`);
                }

                // Track progress
                xhr.upload.onprogress = (event) => {
                    if (event.lengthComputable) {
                        const percentCompleted = Math.round((event.loaded * 100) / event.total);
                        setUploadProgress(percentCompleted);
                    }
                };

                xhr.onload = () => {
                    let data = {};
                    try {
                        data = xhr.responseText ? JSON.parse(xhr.responseText) : {};
                    } catch (err) {
                        console.error("Error parsing upload response:", err);
                    }

                    if (xhr.status >= 200 && xhr.status < 300) {
                        onChange(row, "_file_name", data?.payload);
                        setUploadStatus("success");
                        resolve(true);
                        return;
                    }

                    console.error("Error uploading file:", xhr);
                    const status = xhr.status || "UNKN";
                    const message = data?.detail;
                    if (message) {
                        props.showSnackbar({ color: "error", duration: 6000, message: `Error (${status}): ${message}` });
                    }
                    setUploadStatus("error");
                    resolve();
                };

                xhr.onerror = () => {
                    console.error("Error uploading file (network error).");
                    props.showSnackbar({ color: "error", duration: 6000, message: "Error uploading file. Please try again." });
                    setUploadStatus("error");
                    resolve();
                };

                xhr.send(formData);
            } catch (error) {
                console.error("Error uploading file:", error);
                setUploadStatus("error");
                resolve();
            }
        });
    };

    // Handle source type selection
    const handleSourceSelect = (type) => {
        setSourceType(type);

        switch (type) {
            case "youtube":
                // Default prefix for YouTube URLs
                onChange(row, "url", "");
                break;
            case "vimeo":
                // Default prefix for Vimeo URLs
                onChange(row, "url", "");
                break;
            case "upload":
                onChange(row, "url", null);
                break;
            default:
                break;
        }
    };

    // Create the source selection step
    const step1 = () => {
        const sourceCardStyle = {
            cursor: "pointer",
            height: "110px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.3s",
            "&:hover": {
                transform: "translateY(-5px)",
                boxShadow: "0 8px 16px rgba(0, 0, 0, 0.2)"
            },
            border: "2px solid #e0e0e0",
            position: "relative"
        };

        const createCard = (icon, title, subtitle, color, disabled = false) => (
            <Card
                sx={{
                    ...sourceCardStyle,
                    opacity: disabled ? 0.6 : 1,
                    cursor: disabled ? "not-allowed" : "pointer"
                }}
                onClick={() => {
                    if (!disabled) handleSourceSelect(title.toLowerCase());
                }}
                style={{
                    borderColor: sourceType === title.toLowerCase() ? color : "#e0e0e0"
                }}>
                <CardContent>
                    <Box sx={{ textAlign: "center" }}>
                        {icon}

                        <Typography variant="body" component="div" sx={{ mt: 1 }}>
                            {title}
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                            {subtitle}
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );

        return (
            <Container style={{ margin: 0, padding: 0 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TransparentFragment value={`Select the source type for your resource. You can add content from YouTube, Vimeo, or upload your own audio/video files.`} />
                    </Grid>

                    {/* YouTube */}
                    <Grid item xs={12} md={4}>
                        {createCard(<YouTubeIcon sx={{ fontSize: 24, color: "#FF0000", verticalAlign: "top" }} />, "YouTube", "Via URL", "#FF0000")}
                    </Grid>

                    {/* Vimeo */}
                    <Grid item xs={12} md={4}>
                        {createCard(<OndemandVideoIcon sx={{ fontSize: 24, color: "#1AB7EA", verticalAlign: "top" }} />, "Vimeo", "Via URL", "#1AB7EA")}
                    </Grid>

                    {/* Upload */}
                    <Grid item xs={12} md={4}>
                        {createCard(<UploadFileIcon sx={{ fontSize: 24, color: "#4CAF50", verticalAlign: "top" }} />, "Upload", "MP3, MP4, WAV, etc.", "#4CAF50")}
                    </Grid>
                </Grid>
            </Container>
        );
    };

    // Create the step components.
    const stepUrl = () => {
        const youtubeDescription = `Please input the direct URL of the YouTube video or playlist. For example, "https://www.youtube.com/watch?v=..." or "https://www.youtube.com/playlist?list=..."`;
        const vimeoDescription = `Please input the direct URL of the Vimeo video. For example, "https://player.vimeo.com/video/..."\nIn case of an embedded video, you may need to provide a referer URL make it accessible.`;

        return (
            <Container style={{ margin: 0, padding: 0 }}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <TransparentFragment value={sourceType === "youtube" ? youtubeDescription : vimeoDescription} />
                    </Grid>

                    <Grid item xs={12} mb={2}>
                        <CustomInput name="url" row={row} onChange={onChange} label="URL" autoFocus />
                    </Grid>

                    {sourceType === "vimeo" && (
                        <Grid item xs={12} mb={2}>
                            <CustomInput name="_referer_url" row={row} onChange={onChange} label="Referer URL" />
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <CustomInput name="type" row={row} onChange={onChange} label="Type" select={RESOURCE_TYPES} />
                    </Grid>
                </Grid>
            </Container>
        );
    };

    const step2 = () => {
        return (
            <Container style={{ margin: 0, padding: 0 }}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <TransparentFragment
                            value={`Select the resource authors from the list below.
                            ${sourceType === "youtube" ? "Leave empty to let the system automatically detect the speakers." : ""}`}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <CustomSelection
                            {...props}
                            multiselect
                            callback={handleAuthors}
                            query={querySelectAuthors}
                            getPrimarySearchText={(el) => el?.first_name}
                            getSecondarySearchText={(el) => el?.last_name}
                            getPrimaryText={(el) => `${el?.first_name} ${el?.last_name}`}
                            getSecondaryText={(el) => getEllipsis(el?.bio, 50)}
                        />
                    </Grid>
                </Grid>
            </Container>
        );
    };

    const step3 = () => {
        return (
            <Container style={{ margin: 0, padding: 0 }}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <TransparentFragment value={`Define additional details about the resource.`} />
                    </Grid>

                    <Grid item xs={12}>
                        <CustomInput name="name" row={row} onChange={onChange} label="Name" />
                    </Grid>

                    <Grid item xs={12}>
                        <CustomInput name="description" row={row} onChange={onChange} label="Description" multiline minRows={2} maxRows={5} />
                    </Grid>

                    <Grid item xs={12}>
                        <FormControl variant="standard" sx={{ width: "100%" }}>
                            <FormGroup sx={{ alignContent: "center" }}>
                                <DateTimePicker
                                    label="Uploaded on"
                                    format="MM/DD/YYYY HH:mm"
                                    ampm={true}
                                    value={row.uploaded_on}
                                    onChange={(value) => onChange(row, "uploaded_on", value)}
                                    sx={{ width: "100%" }}
                                />
                            </FormGroup>
                            <FormHelperText sx={{ alignSelf: "left", marginLeft: 2 }}>{row?.form?.uploaded_on?.helperText}</FormHelperText>
                        </FormControl>
                    </Grid>
                </Grid>
            </Container>
        );
    };

    const step4 = () => {
        const placeholder = `# Netscape HTTP Cookie File
# http://curl.haxx.se/rfc/cookie_spec.html
# This file was generated by Cookie-Editor
...`;

        const type = cookieRow.type;

        return (
            <Container style={{ margin: 0, padding: 0 }}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <TransparentFragment
                            value={`Streaming platforms can sometimes flag suspicious IPs and might require authorization to view content. This is common when dealing with public hosting providers or VPNs.

                                This can often be resolved by providing valid cookies along with the request. The most recent cookie was created on ${
                                    formatDate(recentCookie?.[type]?.created_on) || "N/A"
                                }.`}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <CustomInput name="type" row={cookieRow} onChange={onCookieChange} label="Type" select={COOKIE_TYPES} />
                    </Grid>

                    <Grid item xs={12}>
                        <CustomInput
                            name="text"
                            row={cookieRow}
                            onChange={onCookieChange}
                            label="Copy & paste cookies in Netscape format here"
                            multiline
                            minRows={4}
                            maxRows={4}
                            placeholder={placeholder}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <CustomInput name="note" row={cookieRow} onChange={onCookieChange} label="Note" multiline minRows={2} maxRows={5} />
                    </Grid>
                </Grid>
            </Container>
        );
    };

    const step5 = () => {
        const isLocalFile = sourceType === "upload" && selectedFile;

        return (
            <Container style={{ margin: 0, padding: 0 }}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <TransparentFragment
                            value={`Creating new resource with the following details:
                            Name: ${row.name || "N/A"}
                            Type: ${row.type || "N/A"}
                            ${isLocalFile ? `File: ${selectedFile.name}` : `URL: ${row.url || "N/A"}`}
                            Authors: ${row.author_ids.length || "N/A"}
                            ${isLocalFile ? "\nThe file will be converted and transcribed locally. The original file will be deleted after processing.\n" : ""}
                            Click "Apply" to save the resource or "Reset" to start over.`}
                        />
                    </Grid>
                </Grid>
            </Container>
        );
    };

    // Create the file upload step
    const stepFileUpload = () => {
        return (
            <Container style={{ margin: 0, padding: 0 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TransparentFragment
                            value={`You are about to upload ${selectedFile?.name || "a file"}.
                            Click "Upload" to start the process. Proceed to the next step after a successful upload.`}
                        />
                    </Grid>

                    {/* Hidden file input */}
                    <input ref={fileInputRef} type="file" accept="audio/*,video/*" style={{ display: "none" }} onChange={handleFileSelect} />

                    <Grid item xs={12}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                p: 1,
                                border: "1px dashed #ccc",
                                borderRadius: 1
                            }}>
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="subtitle1">{selectedFile?.name ?? "No file selected..."}</Typography>

                                <Typography variant="body2" color="text.secondary">
                                    {selectedFile ? (selectedFile.size / (1024 * 1024)).toFixed(2) + " MB" : ""}
                                </Typography>

                                {uploadStatus === "uploading" && (
                                    <Box sx={{ width: "100%", mt: 1 }}>
                                        <LinearProgress variant="determinate" value={uploadProgress} />

                                        <Typography variant="caption" sx={{ mt: 0.5 }}>
                                            Uploading: {uploadProgress}%
                                        </Typography>
                                    </Box>
                                )}

                                {uploadStatus === "success" && (
                                    <Typography variant="caption" color="success.main" sx={{ mt: 1, display: "block" }}>
                                        File uploaded successfully!
                                    </Typography>
                                )}

                                {uploadStatus === "error" && (
                                    <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
                                        Error uploading file. Please try again.
                                    </Typography>
                                )}
                            </Box>

                            <Button
                                sx={{ ml: 2 }}
                                variant={selectedFile ? "outlined" : "contained"}
                                onClick={() => fileInputRef.current.click()}
                                disabled={uploadStatus === "uploading"}>
                                {selectedFile ? "Change file" : "Select file"}
                            </Button>

                            <Button
                                sx={{ ml: 2 }}
                                variant={selectedFile ? "contained" : "outlined"}
                                onClick={() => handleFileUpload()}
                                disabled={["uploading", "success"].includes(uploadStatus)}>
                                Upload
                            </Button>
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <CustomInput name="type" row={row} onChange={onChange} label="Type" select={RESOURCE_TYPES} />
                    </Grid>
                </Grid>
            </Container>
        );
    };

    // Define the steps and the final step.
    const steps = [
        {
            label: "Source",
            optional: false,
            component: step1(),
            nextDisabled: !sourceType
        },
        {
            label: "URL",
            optional: false,
            component: stepUrl(),
            nextDisabled: !formHasValue(row, "url") || formHasErrors(row, ["name", "description", "uploaded_on"]),
            visible: ["youtube", "vimeo"].includes(sourceType)
        },
        {
            label: "File Upload",
            optional: false,
            component: stepFileUpload(),
            nextDisabled: !selectedFile || uploadStatus !== "success",
            visible: sourceType === "upload"
        },
        {
            label: "Authors",
            optional: false,
            component: step2(),
            nextDisabled: false
        },
        {
            label: "Additional details",
            optional: false,
            component: step3(),
            nextDisabled: formHasErrors(row, ["uploaded_on"]),
            visible: ["upload", "vimeo"].includes(sourceType)
        },
        {
            label: "Authorization",
            optional: false,
            component: step4(),
            nextDisabled: formHasErrors(row, ["uploaded_on"]) || cookieRow?.text?.length < 1,
            visible:
                (sourceType === "youtube" && status?.require_youtube_cookies && isEmpty(recentCookie?.[cookieRow.type])) ||
                (sourceType === "vimeo" && status?.require_vimeo_cookies && isEmpty(recentCookie?.[cookieRow.type]))
        }
    ];

    const final = { component: step5() };

    return (
        <>
            <ConfirmationDialog
                {...props}
                options={{
                    enqueueDialog,
                    setOpen,
                    ...options
                }}
            />

            <CustomStepper steps={steps} final={final} callback={(confirm) => handleClick(confirm)} />
        </>
    );
}
